import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import "./index.css";
import App from "./App.jsx";
import { store } from "@/app/store";
import { initTheme } from "@/lib/theme";
import { applySession, restoreSessionOnBoot } from "@/features/auth/sessionBootstrap";

initTheme();

// The Syncfusion licence is registered inside the two lazy controls that need
// it, not here — see src/lib/syncfusionLicense.js. Registering it at the entry
// pulled the whole ej2 framework chunk in front of first paint.

/**
 * How long first paint waits for `/me`.
 *
 * Long enough that a healthy auth host answers first and the app renders
 * straight into its authenticated state, with no loading screen anywhere in the
 * tree; short enough that a slow one cannot hold the page hostage. Past the
 * budget we render, and the session drops in when it arrives — the static shell
 * in `index.html` is already on screen either way, so nothing flashes.
 */
const FIRST_PAINT_BUDGET_MS = 150;

// Started before React exists, so the request is in flight while the tree is
// still being built rather than waiting for a component to mount and ask.
const session = restoreSessionOnBoot();

// Registered before the race below, so when the request wins, the store is
// already populated by the time we render.
session.then((result) => applySession(store, result));

function render() {
  createRoot(document.getElementById("root")).render(
    <StrictMode>
      <Provider store={store}>
        <App />
      </Provider>
    </StrictMode>,
  );
}

Promise.race([
  session,
  new Promise((resolve) => setTimeout(resolve, FIRST_PAINT_BUDGET_MS)),
]).then(render);
